import * as React from 'react';
import { createPortal } from 'react-dom';
import { css, html } from 'react-strict-dom';
import { Text } from 'src/components';
import { color } from 'src/constants/color.css';
import { useDropdownMutex } from 'src/contexts';

type Props = Readonly<{
  trigger: React.ReactNode;
  children: React.ReactNode;
  anchorSide?: 'left' | 'right';
}>;

export default function Dropdown({ trigger, children, anchorSide }: Props) {
  const { isVisible: open, toggle } = useDropdownMutex();
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(
    null
  );

  React.useEffect(() => {
    if (open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + 8 + window.scrollY,
        left:
          anchorSide === 'right'
            ? rect.right + window.scrollX
            : rect.left + window.scrollX,
      });
    }
  }, [open, anchorSide]);

  return (
    <html.div style={styles.root}>
      <html.div
        ref={triggerRef}
        style={styles.trigger}
        onClick={(e) => {
          toggle();
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {trigger}
      </html.div>
      {open &&
        pos &&
        createPortal(
          <html.div
            style={[
              styles.menu,
              anchorSide === 'right'
                ? styles.posRight(pos.top, pos.left)
                : styles.posLeft(pos.top, pos.left),
            ]}
          >
            {children}
          </html.div>,
          document.body
        )}
    </html.div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <html.div style={styles.sectionHeader}>
      <Text typeface="body.small.medium" color="neutral60">
        {children}
      </Text>
    </html.div>
  );
}

function Row({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <html.div style={styles.row} onClick={onClick}>
      {children}
    </html.div>
  );
}

Dropdown.SectionHeader = SectionHeader;
Dropdown.Row = Row;

const styles = css.create({
  root: {
    display: 'inline-flex',
  },
  trigger: {
    display: 'block',
    cursor: 'pointer',
  },
  menu: {
    position: 'absolute',
    zIndex: 700,
    backgroundColor: color.neutral0,
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    boxShadow: '0px 4px 22px rgba(0, 0, 0, 0.1)',
    minWidth: 220,
    overflow: 'hidden',
  },
  posLeft: (top: number, left: number) => ({
    top,
    left,
  }),
  posRight: (top: number, left: number) => ({
    top,
    right: document.documentElement.clientWidth - left,
  }),
  sectionHeader: {
    paddingTop: 8,
    paddingBottom: 4,
    paddingInline: 12,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    paddingBlock: 12,
    paddingInline: 16,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: color.neutral20,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    ':hover': {
      backgroundColor: color.neutral10,
    },
  },
});
